import {
  IconZoom,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import { Loader, Stack, ActionIcon, Skeleton } from '@mantine/core'
import { useCompetitionStats, useDivisions } from './api'
import { useState, useEffect, Suspense } from 'react'
import LadderComponent from '../stories/Ladder/Ladder'
import { useMediaQuery, useDebouncedValue } from '@mantine/hooks'
import clsx from 'clsx'
import Tabs from '../stories/Tabs/Tabs'
import classes from './Ladder.module.css'
import { useSearchParams } from 'react-router'
import { Typography } from '../common/typography'
import { Dropdown } from '../common/components/dropdown/Dropdown'
import { Input } from '../common/components/input/StyledTextInput'

function MatchesLadderResults({
  competition_id,
  division,
  page,
  pageSize,
  search,
  activeTab,
  selectedDivision,
}: {
  competition_id: number
  division?: number
  page: number
  pageSize: number
  search: string
  activeTab: string
  selectedDivision: string | null
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const { data: statsResponse } = useCompetitionStats({
    competition_id,
    division,
    page,
    page_size: pageSize,
    search,
  })

  const stats = statsResponse.items

  if (stats.length === 0) {
    return (
      <Typography fs="sm" fc={{ light: 'gray-6', dark: 'gray-6' }} ta="center">
        {search ? `No teams found matching "${search}"` : 'No teams found'}
      </Typography>
    )
  }

  return (
    <LadderComponent
      stats={stats}
      active={!isDesktop ? activeTab : undefined}
      divisionLadder={!!selectedDivision}
    />
  )
}

function PaginationControls({
  competition_id,
  division,
  page,
  pageSize,
  search,
}: {
  competition_id: number
  division?: number
  page: number
  pageSize: number
  search: string
}) {
  const [, setSearchParams] = useSearchParams()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const { data: statsResponse } = useCompetitionStats({
    competition_id,
    division,
    page,
    page_size: pageSize,
    search,
  })

  const total = statsResponse.total
  const totalPages = Math.ceil(total / pageSize)
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, total)

  const handlePrevPage = () => {
    if (page > 1) {
      setSearchParams({ page: (page - 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setSearchParams({ page: (page + 1).toString() })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  if (total === 0) return null

  return (
    <div
      className={clsx(classes.paginationContainer, {
        [classes.paginationContainerMobile]: !isDesktop,
      })}
    >
      <Typography fs="sm" lh="140">
        {startItem}-{endItem} of {total}
      </Typography>

      <ActionIcon
        variant="default"
        onClick={handlePrevPage}
        disabled={page === 1}
        className={classes.paginationIcon}
      >
        <IconChevronLeft
          data-disabled={page === 1}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>

      <ActionIcon
        variant="default"
        onClick={handleNextPage}
        disabled={page >= totalPages}
        className={classes.paginationIcon}
      >
        <IconChevronRight
          data-disabled={page >= totalPages}
          className={classes.paginationInnerIcon}
        />
      </ActionIcon>
    </div>
  )
}

export default function MatchesLadder({
  competition_id,
}: {
  competition_id: number
}) {
  const [selectedDivision, setSelectedDivision] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = Number(searchParams.get('page')) || 1
  const pageSize = 20

  const { data: divisions } = useDivisions({
    competition_id: competition_id,
  })

  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [searchValue, setSearchValue] = useState('')
  const [activeTab, setActiveTab] = useState<string>('full')

  const [debouncedSearch] = useDebouncedValue(searchValue, 500)

  useEffect(() => {
    if (currentPage !== 1) {
      setSearchParams({ page: '1' })
    }
  }, [debouncedSearch, selectedDivision])

  const handleDivisionChange = (value: string | null) => {
    setSelectedDivision(value ?? '')
  }

  const tabs = [
    {
      value: 'full',
      label: 'Full view',
    },
    {
      value: 'short',
      label: 'Short view',
    },
    {
      value: 'form',
      label: 'Form',
    },
  ]

  return (
    <div className={classes.container_wrapper}>
      <div
        className={clsx(classes.container_wrapper, {
          [classes.container_wrapper_mobile]: !isDesktop,
        })}
      >
        <div
          className={clsx(classes.search_wrapper, {
            [classes.search_wrapper_mobile]: !isDesktop,
          })}
        >
          <div className={classes.filtersContainer}>
            {divisions && divisions.length > 0 && (
              <Dropdown
                placeholder="All divisions"
                value={selectedDivision}
                onChange={handleDivisionChange}
                data={[
                  { value: '', label: 'All divisions' },
                  ...divisions.map((division) => ({
                    value: division.division_id.toString(),
                    label: division.name,
                  })),
                ]}
                styles={{
                  root: { maxWidth: '15rem' },
                }}
              />
            )}

            <Input
              placeholder="Search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.currentTarget.value)}
              rightSection={<IconZoom className={classes.zoom_icon} />}
              rightSectionPointerEvents="none"
              styles={{
                root: { maxWidth: '15rem' },
              }}
            />
          </div>

          <Suspense
            fallback={
              <Skeleton height={32} width={150} style={{ flexShrink: 0 }} />
            }
          >
            <PaginationControls
              competition_id={competition_id}
              division={selectedDivision ? Number(selectedDivision) : undefined}
              page={currentPage}
              pageSize={pageSize}
              search={debouncedSearch}
            />
          </Suspense>
        </div>

        {!isDesktop && (
          <Tabs
            items={tabs}
            value={activeTab}
            onChange={setActiveTab}
            isSmall
          />
        )}

        <Suspense
          fallback={
            <Stack w="full" h="400px" justify="center" align="center">
              <Loader color="#0B6B7B" />
            </Stack>
          }
        >
          <MatchesLadderResults
            competition_id={competition_id}
            division={selectedDivision ? Number(selectedDivision) : undefined}
            page={currentPage}
            pageSize={pageSize}
            search={debouncedSearch}
            activeTab={activeTab}
            selectedDivision={selectedDivision}
          />
        </Suspense>
      </div>
    </div>
  )
}
